import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import PrivateUser from "../../dtos/user";
import UserValidationService from "../../service/user/validation";
import { useEffect, useState } from "react";
import SearchBox from "../../components/inputs/search-box";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { IMAGE_HOST } from "../../server/config";
import TextInput from "../../components/inputs/text";
import SwitchInput from "../../components/inputs/checkbox/switch";
import { CircularProgress } from "@mui/material";
import InputDialog from "../../components/dialog";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import TransmedProcessService from "../../service/transmed/process";
import TransmedAdmin from "../../dtos/transmed/process/admin";

export const path = "validation/";

export async function loader(): Promise<[PrivateUser[], TransmedAdmin]> {
  const users = new UserValidationService().read.list();
  const admin = new TransmedProcessService().read.admin();
  return [await users, await admin];
}

export function Component() {
  const service = new UserValidationService();
  const [users, admin] = useLoaderData() as [PrivateUser[], TransmedAdmin];

  const [userId, setUserId] = useState<number | null>(null);
  const [answer, setAnswer] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const revalidator = useRevalidator();
  const setError = useSetError();

  const [search, setSearch] = useState<string>("");
  const [quick, setQuick] = useState<boolean>(false);

  const [dialogCallback, setDialogCallback] = useState<
    (() => Promise<void> | void) | null
  >(null);
  const [action, setAction] = useState<"validate" | "return" | "delete">(
    "validate"
  );

  useEffect(() => {
    if (quick && dialogCallback !== null && action !== "delete") {
      dialogCallback();
      setDialogCallback(null);
    }
  }, [action, dialogCallback, quick]);

  const { width } = useWindowDimensions();

  return (
    <>
      <h1>Validare utilizatori</h1>
      <SearchBox
        onSearch={(search) => setSearch(search)}
        placeholder="Caută utilizatori..."
      />
      <div>
        <SwitchInput
          style={{ flex: 1 }}
          label="Validare rapidă"
          checked={quick}
          onChange={(e) => setQuick(e.target.checked)}
        />
        <div className="label">
          Activează confirmarea automată în timpul validării. Se recomandă doar
          pentru volume mari de utilizatori de validat și doar celor care au
          validat întâi în modul clasic (cu ferestre de confirmare) pentru
          prevenirea operațiunilor accidentale nereversibile.
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap-small)",
        }}
      >
        {users
          .filter((user) =>
            (user.first_name + " " + user.last_name)
              .toLowerCase()
              .includes(search.toLowerCase())
          )
          .map((user) => (
            <div
              className="surface"
              key={user.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gap-small)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <h3
                  style={{
                    wordBreak: "break-word",
                    flex: 1,
                    textAlign: "left",
                  }}
                >
                  {user.first_name + " " + user.last_name}
                </h3>
                <div
                  className="label"
                  style={{
                    padding: "var(--gap-xs) var(--gap-small)",
                    borderRadius: 1000,
                    backgroundColor: user.valid
                      ? "green"
                      : user.validation_message
                      ? "var(--color-dark)"
                      : "yellow",
                    color: user.valid
                      ? "white"
                      : user.validation_message
                      ? "white"
                      : "var(--color-dark)",
                  }}
                >
                  {user.valid
                    ? "Validat"
                    : user.validation_message
                    ? "Returnat"
                    : "Nevalidat"}
                </div>
                <button
                  className="primary button text"
                  onClick={() => {
                    setUserId(user.id === userId ? null : user.id);
                    setAnswer(user.validation_message ?? "");
                  }}
                >
                  <FontAwesomeIcon
                    icon={user.id === userId ? faChevronUp : faChevronDown}
                  />
                </button>
              </div>
              {user.id === userId && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: width > 1000 ? "row" : "column",
                    flexWrap: "wrap",
                    alignItems: "stretch",
                    justifyContent: "stretch",
                    gap: "var(--gap-small)",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "var(--gap-xs)",
                    }}
                  >
                    <div>
                      Nume de familie: <b>{user.last_name}</b>
                    </div>
                    <div>
                      Prenume: <b>{user.first_name}</b>
                    </div>
                    <div style={{ height: "var(--gap-small)" }} />
                    <div>
                      Specializare:{" "}
                      <b>
                        {user.is_medicine ? "Medicină" : user.programme_title}
                      </b>
                    </div>
                    <div>
                      An: <b>{user.year}</b>
                    </div>
                    <div>
                      Serie: <b>{user.series}</b>
                    </div>
                    <div>
                      Grupă: <b>{user.group}</b>
                    </div>
                    <div style={{ height: "var(--gap-small)" }} />
                    <div>
                      Număr de înmatriculare: <b>{user.registration_number}</b>
                    </div>
                    <div
                      className="stretcher"
                      style={{ height: "var(--gap-big)" }}
                    />
                    <form
                      style={{
                        alignSelf: "stretch",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: "var(--gap-small)",
                      }}
                      onSubmit={(e) => {
                        e.preventDefault();
                        setAction("validate");
                        setError("");
                        setDialogCallback(() => async () => {
                          setLoading(true);
                          try {
                            await service.update.validate(user.id);
                          } catch (error) {
                            setError(error);
                          }
                          revalidator.revalidate();
                          setLoading(false);
                        });
                      }}
                    >
                      <TextInput
                        disabled={!admin.validate}
                        multiline
                        rows={3}
                        label="Mesaj returnare"
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        style={{
                          flex: 1,
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "var(--gap-small)",
                        }}
                      >
                        {admin.validate && (
                          <>
                            <button
                              disabled={user.valid}
                              type="submit"
                              className="primary button"
                              style={{ margin: 0, ...{ "--color": "green" } }}
                            >
                              <FontAwesomeIcon icon={faCheck} /> Validează
                            </button>
                            <button
                              disabled={user.validation_message === answer}
                              className="secondary button"
                              type="button"
                              onClick={() => {
                                setAction("return");
                                setError("");
                                setDialogCallback(() => async () => {
                                  setLoading(true);
                                  try {
                                    if (answer.length === 0) {
                                      throw new Error(
                                        "Mesajul de returnare nu poate fi gol."
                                      );
                                    }

                                    await service.update.return(
                                      user.id,
                                      answer
                                    );
                                  } catch (error) {
                                    setError(error);
                                  }
                                  revalidator.revalidate();
                                  setLoading(false);
                                });
                              }}
                            >
                              <FontAwesomeIcon icon={faUndo} /> Returnează
                            </button>
                            <button
                              className="primary button"
                              style={{ margin: 0, ...{ "--color": "red" } }}
                              type="button"
                              onClick={() => {
                                setAction("delete");
                                setError("");
                                setDialogCallback(() => async () => {
                                  setLoading(true);
                                  try {
                                    await service.delete.user(user.id);
                                  } catch (error) {
                                    setError(error);
                                  }
                                  revalidator.revalidate();
                                  setLoading(false);
                                });
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} /> Șterge contul
                            </button>
                          </>
                        )}
                        {(loading || revalidator.state === "loading") && (
                          <CircularProgress />
                        )}
                      </div>
                    </form>
                  </div>
                  <div
                    className="surface"
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "stretch",
                      justifyContent: "stretch",
                      objectFit: "contain",
                      objectPosition: "center",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        flex: "1",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        objectFit: "contain",
                        objectPosition: "center",
                        minWidth: "200px",
                      }}
                    >
                      <img
                        src={IMAGE_HOST + user.id_image}
                        alt="Inexistent"
                        style={{
                          objectFit: "contain",
                          objectPosition: "center",
                          width: "100%",
                          maxHeight:
                            "calc(100dvh - var(--nav-height) - 2 * var(--surface-padding) - 2* var(--gap-small))",
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )) ?? <div className="label">Nu există utilizatori.</div>}
      </div>
      <InputDialog
        title="Acțiune ireversibilă"
        continue_label="Continuă"
        abort_label="Anulează"
        dialogCallback={dialogCallback}
        onClose={() => setDialogCallback(null)}
      >
        {action === "validate" ? (
          <>
            Odată validat, utilizatorul va avea acces la toate
            funcționalitățile. Ești sigur că vrei să faci aceasta?
          </>
        ) : action === "return" ? (
          <>
            Utilizatorul nu va putea fi revalidat decât după ce acesta anunță
            corectarea problemelor. Ești sigur că vrei să faci aceasta?
          </>
        ) : (
          <>
            Dacă au fost probleme la validare, poți alege{" "}
            <b>opțiunea de returnare</b>, astfel încât utilizatorul să poată
            corecta aceste probleme. Ștergerea contului este definitiv
            ireversibilă și îți recomandăm să alegi această variantă doar ca
            ultimă soluție. Ești sigur că vrei să faci aceasta?
          </>
        )}
      </InputDialog>
    </>
  );
}
Component.displayName = "page-account-validation";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-validation-error";
